import React, {
  ComponentType,
  MutableRefObject,
  SyntheticEvent,
  useContext,
  useRef,
  useState
} from "react";

export interface NavigationContextModel {
  blockClick: boolean;
  setBlockClick: (block: boolean) => void;
  blockClickCallback: () => void;
  setBlockClickCallback: (callback: () => void) => void;
  blockActions: boolean;
  setBlockActions: (block: boolean) => void;
  pendingActionRef: MutableRefObject<() => void>;
}

export interface WithClickBlockerProps {
  handleBlockableLinkClick: (e: SyntheticEvent) => boolean;
  handleBlockableClick: (callback: () => void) => void;
}

export const BlockableClickContext =
  React.createContext<NavigationContextModel>({
    blockClick: false,
    setBlockClick: (block: boolean) => {
      return null;
    },
    blockClickCallback: null,
    setBlockClickCallback: (callback: () => void) => {
      return null;
    },
    blockActions: false,
    setBlockActions: (block: boolean) => {
      return null;
    },
    pendingActionRef: { current: null }
  });

export const useClickBlockerContext = () => useContext(BlockableClickContext);

export const BlockableClickContextProvider = props => {
  const { children } = props;
  const [blockClick, setBlockClick] = useState<boolean>(false);
  const [blockClickCallback, setBlockClickCallback] =
    useState<() => void>(null);
  const [blockActions, setBlockActions] = useState(false);
  const pendingActionRef = useRef<() => void>(null);

  return (
    <BlockableClickContext.Provider
      value={{
        blockClick,
        setBlockClick,
        blockClickCallback,
        setBlockClickCallback,
        blockActions,
        setBlockActions,
        pendingActionRef
      }}
    >
      {children}
    </BlockableClickContext.Provider>
  );
};

export const withClickBlocker =
  <P,>(Component: ComponentType<P & WithClickBlockerProps>): React.FC<P> =>
  (props: P) => {
    const { blockClick, blockClickCallback, blockActions, pendingActionRef } =
      useClickBlockerContext();

    const handleBlockableLinkClick = e => {
      if (blockClick && blockClickCallback) {
        e.preventDefault();
        pendingActionRef.current = null;
        blockClickCallback();
        return true;
      }
      return false;
    };

    const handleBlockableClick = (callback: () => void) => {
      if (blockActions && blockClick && blockClickCallback) {
        pendingActionRef.current = callback;
        blockClickCallback();
      } else {
        callback();
      }
    };

    return (
      <Component
        {...props}
        handleBlockableLinkClick={handleBlockableLinkClick}
        handleBlockableClick={handleBlockableClick}
      />
    );
  };
